import {
  ArrowDown,
  ArrowUp,
  Copy,
  Eye,
  GripVertical,
  Layers,
  Trash2,
} from "lucide-react";
import * as React from "react";

import type { FieldType, FormFieldSchema } from "@/api/forms";
import { LockLevelBadge } from "@/components/form-builder/LockLevelBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  FIELD_TYPE_LABELS,
  type FieldPath,
  useFormBuilderStore,
} from "@/stores/formBuilder";

interface Props {
  field: FormFieldSchema;
  path: FieldPath;
  depth?: number;
  /** Mở dialog override field — root-level fields only (P2-S5). */
  onOverride?: (path: FieldPath, field: FormFieldSchema) => void;
}

export function FieldNode({ field, path, depth = 0, onOverride }: Props): JSX.Element {
  const selectedPath = useFormBuilderStore((s) => s.selectedPath);
  const selectField = useFormBuilderStore((s) => s.selectField);
  const moveField = useFormBuilderStore((s) => s.moveField);
  const removeField = useFormBuilderStore((s) => s.removeField);
  const duplicateField = useFormBuilderStore((s) => s.duplicateField);
  const addField = useFormBuilderStore((s) => s.addField);

  const isSelected = selectedPath === path;
  const isContainer = field.type === "repeater" || field.type === "section";

  const onDropForRepeater = (e: React.DragEvent): void => {
    if (!isContainer) return;
    e.preventDefault();
    e.stopPropagation();
    const t = e.dataTransfer.getData("application/x-field-type") as FieldType;
    if (t) addField(path, t);
  };

  return (
    <div
      style={{ marginLeft: depth * 16 }}
      className={cn(
        "group rounded-md border bg-background transition-all",
        isSelected
          ? "ring-2 ring-primary border-primary shadow-sm"
          : "hover:border-primary/40 hover:shadow-sm",
      )}
      onClick={(e) => {
        e.stopPropagation();
        selectField(path);
      }}
      onDragOver={(e) => {
        if (isContainer) {
          e.preventDefault();
          e.dataTransfer.dropEffect = "copy";
        }
      }}
      onDrop={onDropForRepeater}
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <GripVertical className="size-4 text-muted-foreground shrink-0 opacity-50 group-hover:opacity-100 transition-opacity" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm truncate">
              {field.label || field.name}
            </span>
            {field.required ? (
              <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                bắt buộc
              </Badge>
            ) : null}
            {field.show_if ? (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                <Eye className="size-3 mr-0.5" /> điều kiện
              </Badge>
            ) : null}
            {(field as { lock_level?: string }).lock_level &&
            (field as { lock_level?: string }).lock_level !== "any" ? (
              <LockLevelBadge level={(field as { lock_level?: string }).lock_level} />
            ) : null}
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <code className="bg-muted px-1 rounded text-[10px]">{field.name}</code>
            <span>·</span>
            <span>{FIELD_TYPE_LABELS[field.type as FieldType]}</span>
            {field.binding?.erp_model ? (
              <>
                <span>·</span>
                <code className="text-[10px]">{field.binding.erp_model}</code>
              </>
            ) : null}
          </div>
        </div>
        <div
          className={cn(
            "flex items-center gap-0.5 shrink-0 transition-opacity",
            isSelected
              ? "opacity-100"
              : "opacity-0 group-hover:opacity-100 focus-within:opacity-100",
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            size="icon"
            variant="ghost"
            className="size-7"
            onClick={() => moveField(path, "up")}
            title="Lên"
          >
            <ArrowUp className="size-3.5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="size-7"
            onClick={() => moveField(path, "down")}
            title="Xuống"
          >
            <ArrowDown className="size-3.5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="size-7"
            onClick={() => duplicateField(path)}
            title="Nhân bản"
          >
            <Copy className="size-3.5" />
          </Button>
          {onOverride && depth === 0 ? (
            <Button
              size="icon"
              variant="ghost"
              className="size-7"
              onClick={() => onOverride(path, field)}
              title="Override 4-layer"
            >
              <Layers className="size-3.5" />
            </Button>
          ) : null}
          <Button
            size="icon"
            variant="ghost"
            className="size-7 text-destructive hover:text-destructive"
            onClick={() => {
              if (confirm(`Xoá field "${field.label || field.name}"?`)) {
                removeField(path);
              }
            }}
            title="Xoá"
          >
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      </div>

      {isContainer && field.fields ? (
        <div className="pl-6 pr-3 pb-3 space-y-2 border-t bg-muted/20">
          <div className="text-[10px] uppercase text-muted-foreground pt-2">
            {field.type === "repeater" ? "Sub-fields (mỗi dòng)" : "Children"}
          </div>
          {field.fields.length === 0 ? (
            <div className="text-xs text-muted-foreground italic py-2">
              Kéo thả field vào đây hoặc click "+" trong properties.
            </div>
          ) : (
            field.fields.map((sub, idx) => (
              <FieldNode
                key={`${path}.fields.${idx}`}
                field={sub}
                path={`${path}.fields.${idx}`}
                depth={depth + 1}
                onOverride={onOverride}
              />
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
